import { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { LatLngBoundsExpression, Icon } from 'leaflet';
import { Workorder, WorkorderProgress } from '@certhon/models';
import { WorkorderMapPin } from './WorkorderMapPin';
import { geocodeWorkorderAddresses } from '../workorders-map.api';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

// Component to update map bounds when they change
function BoundsUpdater({ bounds }: { bounds: LatLngBoundsExpression }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);

  return null;
}

// Fix for default marker icon in Leaflet with bundlers
// @ts-expect-error - Leaflet icon fix
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Status color mapping
const STATUS_COLORS: Record<WorkorderProgress, string> = {
  concept: '#9ca3af', // gray-400
  scheduled: '#3b82f6', // blue-500
  scheduled_non_digital: '#3b82f6', // blue-500
  completed: '#10b981', // green-500
  cancelled: '#6b7280', // gray-500
};

interface WorkordersMapProps {
  workorders: Workorder[];
  hoveredId?: number | null;
  onPinHover?: (recnum: number | null) => void;
  onPinClick?: (workorder: Workorder) => void;
  className?: string;
}

export function WorkordersMap({
  workorders,
  hoveredId,
  onPinHover,
  onPinClick,
  className = '',
}: WorkordersMapProps) {
  // Geocode addresses using the routing API
  const {
    data: coordinatesMap,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'workorders',
      'geocode',
      workorders.map((wo) => `${wo.recnum}:${wo.location_address}`).join(','),
    ],
    queryFn: () => geocodeWorkorderAddresses(workorders),
    staleTime: 60 * 60 * 1000, // 1 hour (addresses don't change often)
    enabled: workorders.length > 0,
    throwOnError: false, // Don't crash the panel on geocode errors
  });

  // Show toast on error (but don't crash)
  useEffect(() => {
    if (error) {
      toast.error('Could not load map locations', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [error]);

  // Calculate map bounds to fit all workorders
  const bounds = useMemo<LatLngBoundsExpression>(() => {
    if (!coordinatesMap || coordinatesMap.size === 0) {
      // Default to Netherlands center
      return [
        [52.0, 4.0],
        [52.5, 5.0],
      ];
    }

    const coords = Array.from(coordinatesMap.values());

    if (coords.length === 1) {
      // Single workorder, add some padding
      return [
        [coords[0][0] - 0.01, coords[0][1] - 0.01],
        [coords[0][0] + 0.01, coords[0][1] + 0.01],
      ];
    }

    return coords as LatLngBoundsExpression;
  }, [coordinatesMap]);

  if (isLoading) {
    return (
      <div
        className={`${className} bg-muted/50 flex items-center justify-center`}
      >
        <p className="text-muted-foreground text-sm">Loading map...</p>
      </div>
    );
  }

  // Handle errors distinctly from empty data
  if (error) {
    return (
      <div
        className={`${className} bg-muted/50 flex flex-col items-center justify-center gap-2`}
      >
        <p className="text-destructive text-sm font-medium">
          Failed to load map data
        </p>
        <p className="text-muted-foreground text-xs">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (!coordinatesMap || coordinatesMap.size === 0) {
    return (
      <div
        className={`${className} bg-muted/50 flex items-center justify-center`}
      >
        <p className="text-muted-foreground text-sm">
          No workorders with valid addresses
        </p>
      </div>
    );
  }

  return (
    <MapContainer
      bounds={bounds}
      boundsOptions={{ padding: [20, 20] }}
      className={className}
      scrollWheelZoom={false}
      style={{ minHeight: '300px' }}
    >
      <BoundsUpdater bounds={bounds} />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />

      {/* Workorder pins */}
      {workorders.map((wo) => {
        const position = coordinatesMap.get(wo.recnum);
        if (!position) return null;

        const color = STATUS_COLORS[wo.progress] || STATUS_COLORS.scheduled;
        const highlighted = hoveredId === wo.recnum;

        return (
          <WorkorderMapPin
            key={wo.recnum}
            workorder={wo}
            position={position}
            color={color}
            highlighted={highlighted}
            onHover={(entering) => {
              if (onPinHover) {
                onPinHover(entering ? wo.recnum : null);
              }
            }}
            onClick={() => {
              if (onPinClick) {
                onPinClick(wo);
              }
            }}
          />
        );
      })}
    </MapContainer>
  );
}
